<template>
  <MarketingPageTemplate
    :title="data.title || 'E-Mail- und Newsletter-Marketing'"
    :subtitle="data.subtitle || 'Versand, Öffnungen und Klicks nach Mailing und Zeitraum'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade E-Mail-Daten…"
    @retry="reload"
  >
    <template #channel-filters>
      <div class="fpair">
        <span class="flabel">Mailing</span>
        <MultiSelectDropdown
          label="Mailing"
          all-label="Alle Mailings"
          :options="mailingOptions"
          :model-value="selectedMailings"
          @update:model-value="onMailings"
        />
      </div>
    </template>

    <template #kpis>
      <!-- UX: Die Raten verdienen die größten Kacheln. Formel als Meta-Zeile wie im Finanz-Dashboard. -->
      <MarketingKpis :cards="rateKpis" :columns="3" />
    </template>

    <template #main>
      <!-- UX: 8 Volumen-Kennzahlen als Liste statt 8 Kacheln, die in einer Reihe überliefen.
           Eindeutig und gesamt stehen direkt nebeneinander. -->
      <DashboardSection title="Volumen">
        <KpiTable :columns="VOLUME_COLUMNS" :rows="volumeRows" />
      </DashboardSection>
      <DashboardSection title="Zustellung, Öffnungen und Klicks nach Mailing" :empty="!mailingNames.length">
        <!-- UX: Eindeutige Anzeigenamen ("Woche 1 · Business Transformation · 4.5.") statt
             4 × "Mental Health Newslette…". Voller Name im Tooltip. Kennzahlfarben dunkel → hell. -->
        <MarketingChart
          :categories="mailingNames"
          :full-names="mailingFullNames"
          :series="mailingSeries"
          horizontal
          x-title="Anzahl"
        />
      </DashboardSection>
    </template>

    <DashboardSection title="Entwicklung im Zeitraum" :empty="!dateCategories.length">
      <MarketingChart :categories="dateCategories" :series="dateSeries" datetime x-title="Versanddatum" />
    </DashboardSection>
    <DashboardSection title="Mailings" :empty="!tableRows.length">
      <!-- UX: Eigener Scroll-Container, erste Spalte (Mailing) bleibt fixiert. -->
      <TableScroll label="Mailings, horizontal scrollbar">
        <table class="jr-list">
          <thead>
            <tr>
              <th scope="col">Mailing</th>
              <th scope="col">Versand</th>
              <th scope="col" class="num">Versendet</th>
              <th scope="col" class="num">Zugestellt</th>
              <th scope="col" class="num">Eindeutige Öffner</th>
              <th scope="col" class="num">Eindeutige Klicker</th>
              <th scope="col" class="num">Gesamtzahl der Öffnungen</th>
              <th scope="col" class="num">Gesamtzahl der Klicks</th>
              <th scope="col" class="num">Öffnungsrate</th>
              <th scope="col" class="num">Klickrate</th>
              <th scope="col" class="num">CTOR</th>
              <th scope="col" class="num">Bounces</th>
              <th scope="col" class="num">Abmeldungen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableRows" :key="row.id">
              <td :title="row.name">{{ displayName(row) }}</td>
              <td>{{ formatDate(row.sent_at) }}</td>
              <td class="num">{{ formatNumber(row.sent) }}</td>
              <td class="num">{{ formatNumber(row.delivered) }}</td>
              <td class="num">{{ formatNumber(row.unique_opens) }}</td>
              <td class="num">{{ formatNumber(row.unique_clicks) }}</td>
              <td class="num">{{ formatNumber(row.total_opens) }}</td>
              <td class="num">{{ formatNumber(row.total_clicks) }}</td>
              <td class="num">{{ formatPercent(row.open_rate) }}</td>
              <td class="num">{{ formatPercent(row.click_rate) }}</td>
              <td class="num">{{ formatPercent(row.ctor) }}</td>
              <td class="num">{{ formatNumber(row.bounces) }}</td>
              <td class="num">{{ formatNumber(row.unsubscribes) }}</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import KpiTable from '../components/KpiTable.vue';
import MarketingChart from '../components/MarketingChart.vue';
import MultiSelectDropdown from '../components/MultiSelectDropdown.vue';
import TableScroll from '../components/TableScroll.vue';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import { mailingDisplayNames } from '../composables/displayNames';
import { formatDate, formatNumber, formatPercent } from '../composables/formatters';

const props = defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

const { extraValue, setExtra } = useMarketingFilters();

const selectedMailings = computed(() => {
  const raw = extraValue('mailing_id', '');
  return raw ? String(raw).split(',').filter(Boolean) : [];
});

const extraParams = computed(() => ({
  mailing_id: selectedMailings.value.join(',')
}));

const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/email/', extraParams);

const tableRows = computed(() => data.value.table || []);

const mailingDates = computed(() => {
  const map = {};
  tableRows.value.forEach((row) => {
    map[String(row.id)] = row.sent_at;
  });
  return map;
});

// Anzeigenamen über alle bekannten Mailings (Filteroptionen + Tabelle), damit Filter,
// Diagramm und Tabelle dieselben Namen zeigen.
const displayNames = computed(() => mailingDisplayNames(
  (props.filterOptions.mailings || []).map((item) => ({
    id: item.id,
    name: item.name,
    sent_at: mailingDates.value[String(item.id)]
  })).concat(tableRows.value.filter((row) => !(props.filterOptions.mailings || []).some((item) => String(item.id) === String(row.id))))
));

function displayName(row) {
  return displayNames.value[String(row.id)]?.label || row.name;
}

const mailingOptions = computed(() =>
  (props.filterOptions.mailings || []).map((item) => ({
    value: String(item.id),
    label: displayNames.value[String(item.id)]?.label || item.name
  }))
);

const kpis = computed(() => data.value.kpis || []);
const rateKpis = computed(() => kpis.value.filter((item) => item.format === 'percent'));

function kpi(key) {
  return kpis.value.find((item) => item.key === key) || null;
}

const VOLUME_COLUMNS = [
  { key: 'name', label: 'Kennzahl' },
  { key: 'value', label: 'Wert' }
];

// Reihenfolge und Namen wie im Feedback-Dokument; die Beschriftung kommt aus der API (kpi.label).
const VOLUME_KEYS = ['sent', 'delivered', 'unique_opens', 'unique_clicks', 'total_opens', 'total_clicks', 'bounces', 'unsubscribes'];

const definitions = computed(() => Object.fromEntries((data.value.legend || []).map((item) => [item.kpi_key, item])));

const volumeRows = computed(() => VOLUME_KEYS
  .map((key) => kpi(key))
  .filter(Boolean)
  .map((card) => ({
    key: card.key,
    label: card.label,
    info: definitions.value[card.key]?.definition_de || '',
    values: { value: card }
  })));

// by_mailing hat dieselbe Reihenfolge wie die Tabelle (beide serverseitig aus derselben Liste).
const byMailing = computed(() => (data.value.series?.by_mailing || []).map((item, index) => ({
  ...item,
  id: tableRows.value[index]?.name === item.name ? tableRows.value[index].id : undefined
})));
const mailingNames = computed(() => byMailing.value.map((item) => (
  item.id !== undefined ? displayName({ id: item.id, name: item.name }) : item.name
)));
const mailingFullNames = computed(() => byMailing.value.map((item) => item.name));
const mailingSeries = computed(() => [
  { name: 'Zugestellte E-Mails', data: byMailing.value.map((item) => item.delivered) },
  { name: 'Eindeutige Öffner', data: byMailing.value.map((item) => item.unique_opens) },
  { name: 'Eindeutige Klicker', data: byMailing.value.map((item) => item.unique_clicks) }
]);
const dateCategories = computed(() => (data.value.series?.by_date || []).map((item) => item.date));
const dateSeries = computed(() => [
  { name: 'Zugestellte E-Mails', data: (data.value.series?.by_date || []).map((item) => item.delivered) },
  { name: 'Eindeutige Öffner', data: (data.value.series?.by_date || []).map((item) => item.unique_opens) },
  { name: 'Eindeutige Klicker', data: (data.value.series?.by_date || []).map((item) => item.unique_clicks) }
]);

function onMailings(values) {
  setExtra({ mailing_id: values.join(',') });
}
</script>
